import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MailPlus } from "lucide-react";
import { WhipLogo } from "@/components/WhipLogo";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60";

export default function RequestSetupLink() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-setup/request`, { email: email.trim() });
      setDone(data.message || "If that address has a pending WHIP account, a new setup link is on its way.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send a link right now. Try again in a few minutes.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "var(--whip-bg)" }} data-testid="request-setup-link-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><WhipLogo size={40} /></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 backdrop-blur">
          <div className="flex items-center gap-2 text-orange-400 text-xs font-mono uppercase tracking-widest mb-3"><MailPlus className="w-4 h-4" /> Account setup</div>
          <h1 className="text-2xl font-bold text-white">Request a new setup link</h1>
          <p className="text-sm text-white/55 mt-2">
            Approved for WHIP but your link expired or never arrived? Enter the email you applied with and we will send a fresh single-use link.
          </p>
          {done ? (
            <p className="mt-6 text-sm rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-4 py-3" data-testid="request-setup-link-done">{done}</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input type="email" autoComplete="email" placeholder="you@organization.gov" value={email}
                onChange={(e) => setEmail(e.target.value)} className={inputCls} required data-testid="request-setup-link-email" />
              {error && <p className="text-sm text-rose-300" data-testid="request-setup-link-error">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors disabled:opacity-60"
                data-testid="request-setup-link-submit">
                {submitting ? "Sending…" : "Email me a setup link"}
              </button>
            </form>
          )}
          <Link to="/login" className="block text-center text-xs text-white/45 hover:text-orange-300 mt-6" data-testid="request-setup-link-back">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
